import React, { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, LogOut, Plus } from 'lucide-react';
import {
  listarLaboratorios, listarContratos, listarUsoAtual, listarUsoMensal, montarLinhas,
} from '../../data/admin';
import { listLabMembers } from '../../data/labs';
import { signOut } from '../../data/auth';
import ListaLaboratorios from './ListaLaboratorios';
import DetalheLaboratorio from './DetalheLaboratorio';
import NovoLaboratorio from './NovoLaboratorio';

/**
 * Painel de operação do produto.
 *
 * Fica FORA do FIVSystem de propósito, por dois motivos:
 *
 *   1. `useLicenca` exige vínculo ativo em `lab_members` para emitir licença.
 *      Dentro do FIVSystem, este painel se bloquearia sozinho num laboratório
 *      de cliente — justamente onde ele mais precisa funcionar.
 *
 *   2. Não é uma aba do app do laboratório. É a ferramenta de quem opera o
 *      produto, e misturar as duas é como o /admin acaba vazando para o
 *      cliente.
 *
 * Lista + detalhe em componentes separados porque `LabManagement` já mostrou
 * onde dá esse caminho: 858 linhas por ter acumulado tudo num arquivo só.
 */
const PainelAdmin = ({ user }) => {
  const [linhas, setLinhas]       = useState([]);
  const [selecionado, setSel]     = useState(null);
  const [mensalPorLab, setMensal] = useState({});
  const [semAdmin, setSemAdmin]   = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]           = useState('');
  const [criando, setCriando]     = useState(false);

  /**
   * Três consultas, não N.
   *
   * `countLabContents` (labs.js) faz 4 consultas POR laboratório — com 50 labs
   * seriam 200 requisições para desenhar uma lista. As views de uso trazem
   * tudo de uma vez, e a junção acontece em memória.
   */
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [labs, contratos, uso] = await Promise.all([
        listarLaboratorios(), listarContratos(), listarUsoAtual(),
      ]);
      const montadas = montarLinhas(labs, contratos, uso);
      setLinhas(montadas);

      // Sessões do mês corrente, e quem ficou sem administrador.
      //
      // O segundo existe por causa do onboarding: se a criação do primeiro
      // admin falhar, sobra um laboratório em que ninguém consegue entrar.
      // Estado ruim que aparece na tela é estado que se conserta; o que some,
      // não.
      const mes = {};
      const orfaos = {};
      const atual = new Date().toISOString().slice(0, 7);
      await Promise.all(montadas.map(async (l) => {
        try {
          const m = await listarUsoMensal(l.id);
          mes[l.id] = (m || []).find(x => String(x.mes).slice(0, 7) === atual)?.sessoes ?? 0;
        } catch { mes[l.id] = null; }

        try {
          const membros = await listLabMembers(l.id) || [];
          orfaos[l.id] = !membros.some(m => m.role === 'admin' && m.status === 'active');
        } catch { orfaos[l.id] = false; }
      }));
      setMensal(mes);
      setSemAdmin(orfaos);
    } catch (e) {
      console.error('painel admin:', e);
      setErro(e.message || 'Não foi possível carregar.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const linhaSelecionada = linhas.find(l => l.id === selecionado) || null;

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-5 h-5 text-slate-900 shrink-0" />
            <div className="min-w-0">
              <h1 className="font-semibold text-slate-900 leading-tight">Administração</h1>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setCriando(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm
                         hover:bg-blue-700">
              <Plus size={14} /> Laboratório
            </button>
            <button onClick={carregar} disabled={carregando}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded text-sm
                         text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Recarregar
            </button>
            <button onClick={() => signOut()}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded text-sm
                         text-slate-700 hover:bg-slate-50">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {erro && (
          <div className="bg-white border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
            {erro}
          </div>
        )}

        {carregando && linhas.length === 0 ? (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Carregando laboratórios…</p>
          </div>
        ) : (
          <>
            <ListaLaboratorios
              linhas={linhas}
              selecionadoId={selecionado}
              onSelecionar={(id) => setSel(id === selecionado ? null : id)}
              usoMensalPorLab={mensalPorLab}
              semAdminPorLab={semAdmin}
            />

            {linhaSelecionada
              ? <DetalheLaboratorio linha={linhaSelecionada} onSalvou={carregar} />
              : linhas.length > 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  Clique num laboratório para ver e editar o contrato.
                </p>
              )}
          </>
        )}
      </main>

      {criando && (
        <NovoLaboratorio
          onPronto={carregar}
          onFechar={() => setCriando(false)}
        />
      )}
    </div>
  );
};

export default PainelAdmin;
